import { useEffect, useState } from "react";
import { Star, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { useSettings } from "@/context/SettingsContext";

const GoogleLogo = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.3H12v4.4h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.2-2.1 3.5-5.1 3.5-8.7z" />
    <path fill="#34A853" d="M12 24c3.2 0 6-1.1 8-2.9l-3.9-3c-1.1.7-2.5 1.2-4.1 1.2-3.1 0-5.8-2.1-6.7-5H1.3v3.1C3.3 21.3 7.3 24 12 24z" />
    <path fill="#FBBC05" d="M5.3 14.3c-.2-.7-.4-1.5-.4-2.3s.1-1.6.4-2.3V6.6H1.3C.5 8.2 0 10 0 12s.5 3.8 1.3 5.4l4-3.1z" />
    <path fill="#EA4335" d="M12 4.7c1.8 0 3.3.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.3 0 3.3 2.7 1.3 6.6l4 3.1c.9-2.9 3.6-5 6.7-5z" />
  </svg>
);

export function useGoogleReviews() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/google-reviews").then(({ data }) => setData(data)).catch(() => setData({ reviews: [] })); }, []);
  return data;
}

export function GoogleReviewCard({ r, index }) {
  const { tl } = useI18n();
  return (
    <div data-testid={`google-review-${index}`} className="flex flex-col rounded-2xl border border-border bg-card p-6">
      <div className="flex items-center gap-3">
        {r.photo ? <img src={r.photo} alt="" referrerPolicy="no-referrer" className="h-10 w-10 rounded-full object-cover" />
          : <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 font-display font-bold text-primary">{(r.author || "?")[0]}</div>}
        <div className="min-w-0">
          <div className="truncate font-display font-semibold">{r.author}</div>
          <div className="text-xs text-muted-foreground">{r.relative_time}</div>
        </div>
        <GoogleLogo />
      </div>
      <div className="mt-3 flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} className={`h-4 w-4 ${j < (r.rating || 0) ? "fill-primary text-primary" : "text-border"}`} />)}</div>
      <p className="mt-3 flex-1 text-sm leading-relaxed">{r.text}</p>
      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">{tl({ es: "Ver en Google", en: "View on Google", fr: "Voir sur Google" })} <ExternalLink className="h-3 w-3" /></a>}
    </div>
  );
}

export function GoogleReviewsSection({ title, compact = false }) {
  const { tl } = useI18n();
  const { settings } = useSettings();
  const data = useGoogleReviews();
  const reviews = (data?.reviews || []).filter((r) => r.text).slice(0, compact ? 3 : 6);
  if (!reviews.length) return null;
  return (
    <section className="bg-secondary/40 py-16" data-testid="google-reviews-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold tracking-tight md:text-3xl">{title || tl({ es: "Lo que dicen en Google", en: "What people say on Google", fr: "Ce qu'on dit de nous sur Google" })}</h2>
            {data?.rating && (
              <div className="mt-2 flex items-center gap-2 text-sm" data-testid="google-rating">
                <GoogleLogo />
                <span className="flex gap-0.5">{[...Array(5)].map((_, j) => <Star key={j} className="h-4 w-4 fill-primary text-primary" />)}</span>
                <span className="font-semibold">{data.rating}</span>
                <span className="text-muted-foreground">· {data.count} {tl({ es: "reseñas", en: "reviews", fr: "avis" })}</span>
              </div>
            )}
          </div>
          <a href={settings?.google_review_url || data?.maps_url} target="_blank" rel="noopener noreferrer" data-testid="google-reviews-all"
            className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-2.5 text-sm font-semibold transition-transform hover:scale-105">
            <Star className="h-4 w-4 fill-primary text-primary" /> {tl({ es: "Ver todas en Google", en: "See all on Google", fr: "Voir tous les avis" })}
          </a>
        </div>
        <div className="grid gap-6 md:grid-cols-3">{reviews.map((r, i) => <GoogleReviewCard key={r.url || `${r.author}-${r.relative_time}`} r={r} index={i} />)}</div>
      </div>
    </section>
  );
}
